import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import TeamBadge from "@/components/base/TeamBadge";
import StatusBadge from "@/components/base/StatusBadge";
import VideoPlayer from "@/pages/match/components/VideoPlayer";
import LineupCard from "@/pages/match/components/LineupCard";
import { matches } from "@/mocks/matches";
import { buildRoster } from "@/utils/roster";
import { useFavorites } from "@/hooks/useFavorites";
import { formatDateTimeCN, formatViewers } from "@/utils/format";

function splitQuarters(total: number): number[] {
  const ratios = [0.27, 0.25, 0.24, 0.24];
  const parts = ratios.map((r) => Math.round(total * r));
  const diff = total - parts.reduce((a, b) => a + b, 0);
  parts[3] += diff;
  return parts;
}

export default function MatchPage() {
  const { matchId } = useParams();
  const match = matches.find((m) => m.id === matchId);
  const { toggle, isFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(t);
  }, [toast]);

  if (!match) {
    return (
      <div className="w-full px-4 md:px-6 py-24 text-center">
        <i className="ri-tv-2-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-2xl text-foreground-950">该比赛不存在或已下架</h1>
        <p className="mt-2 text-foreground-600">请返回赛程页选择其他正在进行或即将开始的比赛。</p>
        <Link
          to="/schedule"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer"
        >
          <i className="ri-calendar-line" />
          前往赛程列表
        </Link>
      </div>
    );
  }

  const fav = isFavorite(match.id);
  const related = matches
    .filter((m) => m.id !== match.id)
    .sort((a, b) => (b.leagueSlug === match.leagueSlug ? 1 : 0) - (a.leagueSlug === match.leagueSlug ? 1 : 0))
    .slice(0, 4);

  const homeRoster = buildRoster(`${match.home.id}-${match.id}`, match.homeScore);
  const awayRoster = buildRoster(`${match.away.id}-${match.id}`, match.awayScore);
  const homeQ = splitQuarters(match.homeScore);
  const awayQ = splitQuarters(match.awayScore);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("比赛链接已复制，快分享给球友吧");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5 flex-wrap">
        <Link to="/" className="hover:text-primary-700 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={`/category/${match.leagueSlug}`} className="hover:text-primary-700 cursor-pointer">
          {match.leagueName}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">比赛详情</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 lg:gap-8">
        <div className="min-w-0 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading font-semibold text-2xl md:text-3xl text-foreground-950">
                {match.home.name} vs {match.away.name}
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                {match.leagueName} · {match.stage} · {formatDateTimeCN(match.startTime)}
              </p>
            </div>
            <StatusBadge status={match.status} />
          </div>

          <VideoPlayer match={match} />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
            {[
              { icon: "ri-calendar-event-line", label: "比赛时间", value: formatDateTimeCN(match.startTime) },
              { icon: "ri-map-pin-line", label: "比赛地点", value: match.venue },
              { icon: "ri-trophy-line", label: "赛季阶段", value: match.stage },
            ].map((info) => (
              <div key={info.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <div className="flex items-center gap-2 text-foreground-500 text-xs mb-1.5">
                  <i className={`${info.icon} text-accent-600`} />
                  {info.label}
                </div>
                <p className="text-sm text-foreground-900 leading-snug">{info.value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-line-chart-line text-accent-600" />
              比赛进程
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[420px]">
                <thead>
                  <tr className="text-xs text-foreground-500">
                    <th className="text-left font-normal py-2">球队</th>
                    <th className="text-center font-normal py-2">第一节</th>
                    <th className="text-center font-normal py-2">第二节</th>
                    <th className="text-center font-normal py-2">第三节</th>
                    <th className="text-center font-normal py-2">第四节</th>
                    <th className="text-right font-normal py-2">总分</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { team: match.home, q: homeQ, total: match.homeScore },
                    { team: match.away, q: awayQ, total: match.awayScore },
                  ].map((row) => (
                    <tr key={row.team.id} className="border-t border-background-100">
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <TeamBadge shortName={row.team.shortName} color={row.team.color} size="sm" />
                          <span className="text-foreground-900">{row.team.shortName}</span>
                        </div>
                      </td>
                      {row.q.map((v, i) => (
                        <td key={i} className="text-center py-3 text-foreground-700">
                          {v}
                        </td>
                      ))}
                      <td className="text-right py-3 font-heading font-semibold text-lg text-foreground-950">
                        {row.total}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {match.status === "upcoming" && (
              <p className="mt-3 text-xs text-foreground-500">
                比赛尚未开始，以上为演示数据，开赛后实时更新。
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-5">
            <LineupCard
              teamName={match.home.name}
              shortName={match.home.shortName}
              color={match.home.color}
              roster={homeRoster}
            />
            <LineupCard
              teamName={match.away.name}
              shortName={match.away.shortName}
              color={match.away.color}
              roster={awayRoster}
            />
          </div>
        </div>

        <aside className="space-y-5">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-foreground-500">{match.period}</span>
              <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                <i className="ri-eye-line" />
                {formatViewers(match.viewers)} 观看
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-col items-center gap-2 flex-1">
                <TeamBadge shortName={match.home.shortName} color={match.home.color} size="lg" />
                <span className="text-sm text-foreground-900 text-center">{match.home.shortName}</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="font-heading font-semibold text-4xl text-foreground-950">
                  {match.homeScore}
                  <span className="mx-2 text-foreground-300">:</span>
                  {match.awayScore}
                </span>
                <span className="mt-1 text-[11px] font-label text-accent-600">
                  {match.status === "live" ? "实时比分" : match.status === "finished" ? "终场比分" : "尚未开赛"}
                </span>
              </div>
              <div className="flex flex-col items-center gap-2 flex-1">
                <TeamBadge shortName={match.away.shortName} color={match.away.color} size="lg" />
                <span className="text-sm text-foreground-900 text-center">{match.away.shortName}</span>
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  toggle(match.id);
                  setToast(fav ? "已取消收藏" : "已加入收藏，可在本地保留");
                }}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  fav
                    ? "bg-accent-500 text-background-50 dark:text-foreground-950"
                    : "bg-background-100 text-foreground-800 hover:bg-background-200"
                }`}
              >
                <i className={fav ? "ri-star-fill" : "ri-star-line"} />
                {fav ? "已收藏" : "收藏比赛"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-background-300 text-foreground-800 hover:bg-background-100 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-basketball-line text-accent-600" />
              相关比赛推荐
            </h2>
            <div className="space-y-3">
              {related.map((m) => (
                <Link
                  key={m.id}
                  to={`/match/${m.id}`}
                  className="block p-3 rounded-md border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] text-foreground-500">{m.leagueName}</span>
                    <StatusBadge status={m.status} />
                  </div>
                  <div className="flex items-center gap-2">
                    <TeamBadge shortName={m.home.shortName} color={m.home.color} size="sm" />
                    <span className="text-xs text-foreground-800 truncate">{m.home.shortName}</span>
                    <span className="text-xs text-foreground-400 mx-1">vs</span>
                    <span className="text-xs text-foreground-800 truncate">{m.away.shortName}</span>
                    <TeamBadge shortName={m.away.shortName} color={m.away.color} size="sm" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm bb-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}